// Project Data Service - Manages projects using GitHub Gist API for server-side persistence

// GitHub Gist configuration - Replace these with your own values
const GIST_ID = process.env.REACT_APP_GIST_ID || 'your-gist-id-here';
const GITHUB_TOKEN = process.env.REACT_APP_GITHUB_TOKEN || 'your-github-token-here';
const GIST_FILENAME = 'portfolio-projects.json';

// Fallback to localStorage if Gist is not configured
const USE_GIST = GIST_ID !== 'your-gist-id-here' && GITHUB_TOKEN !== 'your-github-token-here';
const STORAGE_KEY = 'portfolio_projects';

// Vercel API proxy functions (avoid CORS issues)
const fetchFromApi = async () => {
  try {
    const response = await fetch('/api/projects');
    if (!response.ok) throw new Error('Failed to fetch from API');
    return await response.json();
  } catch (error) {
    console.warn('Failed to fetch from API, falling back to localStorage:', error);
    return null;
  }
};

const updateViaApi = async (projects) => {
  try {
    const response = await fetch('/api/projects', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ projects })
    });

    if (!response.ok) {
      throw new Error('Failed to update via API');
    }

    return await response.json();
  } catch (error) {
    console.error('Failed to update via API:', error);
    throw error;
  }
};

// Get high-quality logo from domain using Clearbit Logo API
export const getLogoUrl = (url) => {
  try {
    const urlObj = new URL(url);
    const domain = urlObj.hostname.replace('www.', '');
    // Clearbit provides high-quality company logos
    return `https://logo.clearbit.com/${domain}`;
  } catch {
    return null;
  }
};

// Get favicon as fallback
export const getFaviconUrl = (url, size = 128) => {
  try {
    const urlObj = new URL(url);
    const domain = urlObj.hostname;
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=${size}`;
  } catch {
    return null;
  }
};

// Extract app ID from App Store URL and get icon via iTunes API
export const getAppStoreIcon = (appStoreUrl) => {
  try {
    // Extract app ID from URL like: https://apps.apple.com/in/app/dhobigo/id6752208663
    const idMatch = appStoreUrl.match(/\/id(\d+)/);
    if (idMatch && idMatch[1]) {
      const appId = idMatch[1];
      // iTunes Lookup API returns app info including artwork
      // We'll use a proxy-friendly approach with the icon URL pattern
      return `https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/app-icon/${appId}/AppIcon-0-0-1x_U007emarketing-0-7-0-85-220.png/512x512bb.jpg`;
    }
  } catch {
    // Fallback to Apple logo
  }
  return null;
};

// Extract package ID from Play Store URL and construct icon URL
export const getPlayStoreIcon = (playStoreUrl) => {
  try {
    // Extract package name from URL like: https://play.google.com/store/apps/details?id=com.dhobigo
    const packageMatch = playStoreUrl.match(/id=([^&]+)/);
    if (packageMatch && packageMatch[1]) {
      const packageId = packageMatch[1];
      // Use a reliable icon fetching service
      // Option 1: Use Android Asset Studio pattern (reliable for most apps)
      return `https://play-lh.googleusercontent.com/icon?id=${packageId}`;
    }
  } catch {
    // Fallback
  }
  return null;
};

// Extract image URL from demo link automatically
export const getImageFromLink = (demoLink) => {
  if (!demoLink) return null;
  
  try {
    const url = new URL(demoLink);
    const hostname = url.hostname;
    
    // Play Store - try to get actual app icon
    if (hostname.includes('play.google.com')) {
      const packageMatch = demoLink.match(/id=([^&]+)/);
      if (packageMatch && packageMatch[1]) {
        // Use a service that can fetch Play Store icons
        // This uses an image proxy that fetches from Play Store
        return `https://play-lh.googleusercontent.com/a/icon?id=${packageMatch[1]}`;
      }
    }
    
    // App Store - get actual app icon using iTunes artwork pattern
    if (hostname.includes('apps.apple.com')) {
      const idMatch = demoLink.match(/\/id(\d+)/);
      if (idMatch && idMatch[1]) {
        // Use iTunes artwork URL pattern
        return `https://tools.applemediaservices.com/api/artwork/apps/${idMatch[1]}?format=png&size=512`;
      }
    }
    
    // For regular websites, use Clearbit Logo API for high-quality logos
    const domain = hostname.replace('www.', '');
    return `https://logo.clearbit.com/${domain}`;
  } catch {
    return null;
  }
};

// SVG placeholder generator for project images (fallback)
const generatePlaceholderSVG = (text, bgColor = '#3b82f6') => {
  const initials = text.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
  return `data:image/svg+xml,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200">
      <rect width="200" height="200" fill="${bgColor}"/>
      <text x="100" y="115" font-family="Arial, sans-serif" font-size="64" font-weight="bold" fill="white" text-anchor="middle">${initials}</text>
    </svg>
  `)}`;
};

// Default projects to initialize storage
const defaultProjects = [
  {
    id: '1',
    title: 'DhobiGo (Android)',
    description: 'On-demand laundry app for Gwalior with doorstep pickup & delivery, premium steam ironing, transparent pricing, and professional care.',
    // Note: For Play Store apps, you may need to manually add the icon URL from Play Store
    imgPath: 'https://logo.clearbit.com/dhobigo.com',
    demoLink: 'https://play.google.com/store/apps/details?id=com.dhobigo',
    isBlog: false,
    isSelectedWork: true,
    order: 1
  },
  {
    id: '2',
    title: 'DhobiGo (iOS)',
    description: 'DhobiGo on iPhone: reliable, affordable laundry care with pickup, delivery, and premium steam ironing.',
    imgPath: 'https://logo.clearbit.com/dhobigo.com',
    demoLink: 'https://apps.apple.com/in/app/dhobigo/id6752208663',
    isBlog: false,
    isSelectedWork: true,
    order: 2
  },
  {
    id: '3',
    title: 'Survlens (Edge AI Surveillance)',
    description: 'Next-gen surveillance platform with in-house Edge AI chipsets, 60+ video analytics, and real-time alerts for smarter, safer environments.',
    imgPath: 'https://logo.clearbit.com/survlens.com',
    demoLink: 'https://www.survlens.com/',
    isBlog: false,
    isSelectedWork: true,
    order: 3
  },
  {
    id: '4',
    title: 'Diego Sevilla — Developer Portfolio',
    description: 'A polished personal developer site showcasing projects, writing, and clean UX engineering craft.',
    imgPath: 'https://logo.clearbit.com/diegosevilla.dev',
    demoLink: 'https://diegosevilla.dev/',
    isBlog: false,
    isSelectedWork: false,
    order: 4
  }
];

// Initialize storage with default projects if empty
const initializeStorage = () => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(defaultProjects));
    return defaultProjects;
  }
  return JSON.parse(stored);
};

// Get all projects
export const getProjects = async () => {
  try {
    if (USE_GIST) {
      const apiProjects = await fetchFromApi();
      if (apiProjects && Array.isArray(apiProjects)) {
        // Cache in localStorage as backup
        localStorage.setItem(STORAGE_KEY, JSON.stringify(apiProjects));
        return apiProjects.sort((a, b) => a.order - b.order);
      }
    }
  } catch (error) {
    console.warn('Failed to fetch from API, using localStorage:', error);
  }

  // Fallback to localStorage
  const projects = initializeStorage();
  return projects.sort((a, b) => a.order - b.order);
};

// Get single project by ID
export const getProjectById = async (id) => {
  const projects = await getProjects();
  return projects.find(p => p.id === id);
};

// Get selected work projects
export const getSelectedWorkProjects = async () => {
  const projects = await getProjects();
  return projects.filter(p => p.isSelectedWork === true);
};

// Add new project
export const addProject = async (project) => {
  const projects = await getProjects();

  // Auto-generate image from demo link if not provided
  let imgPath = project.imgPath;
  if (!imgPath && project.demoLink) {
    imgPath = getImageFromLink(project.demoLink);
  }

  const newProject = {
    ...project,
    imgPath,
    id: Date.now().toString(),
    order: projects.length + 1,
    isSelectedWork: project.isSelectedWork || false
  };
  projects.push(newProject);

  // Update localStorage
  localStorage.setItem(STORAGE_KEY, JSON.stringify(projects));

  // Sync with API if enabled
  if (USE_GIST) {
    try {
      await updateViaApi(projects);
    } catch (error) {
      console.error('Failed to sync new project to API:', error);
      throw error;
    }
  }

  return newProject;
};

// Update existing project
export const updateProject = async (id, updates) => {
  const projects = await getProjects();
  const index = projects.findIndex(p => p.id === id);
  if (index !== -1) {
    projects[index] = { ...projects[index], ...updates };

    // Update localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify(projects));

    // Sync with API if enabled
    if (USE_GIST) {
      try {
        await updateViaApi(projects);
      } catch (error) {
        console.error('Failed to sync project update to API:', error);
        throw error;
      }
    }

    return projects[index];
  }
  return null;
};

// Delete project
export const deleteProject = async (id) => {
  const projects = await getProjects();
  const filtered = projects.filter(p => p.id !== id);
  // Reorder remaining projects
  const reordered = filtered.map((p, i) => ({ ...p, order: i + 1 }));

  // Update localStorage
  localStorage.setItem(STORAGE_KEY, JSON.stringify(reordered));

  // Sync with API if enabled
  if (USE_GIST) {
    try {
      await updateViaApi(reordered);
    } catch (error) {
      console.error('Failed to sync project deletion to API:', error);
      throw error;
    }
  }

  return true;
};

// Reorder projects
export const reorderProjects = async (orderedIds) => {
  const projects = await getProjects();
  const reordered = orderedIds.map((id, index) => {
    const project = projects.find(p => p.id === id);
    return { ...project, order: index + 1 };
  });

  // Update localStorage
  localStorage.setItem(STORAGE_KEY, JSON.stringify(reordered));

  // Sync with API if enabled
  if (USE_GIST) {
    try {
      await updateViaApi(reordered);
    } catch (error) {
      console.error('Failed to sync project reordering to API:', error);
      throw error;
    }
  }

  return reordered;
};

// Reset to default projects
export const resetToDefaults = () => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(defaultProjects));
  return defaultProjects;
};

// Export projects as JSON (for backup)
export const exportProjects = async () => {
  const projects = await getProjects();
  return JSON.stringify(projects, null, 2);
};

// Import projects from JSON
export const importProjects = async (jsonString) => {
  try {
    const projects = JSON.parse(jsonString);
    if (Array.isArray(projects)) {
      // Update localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify(projects));

      // Sync with API if enabled
      if (USE_GIST) {
        try {
          await updateViaApi(projects);
        } catch (error) {
          console.error('Failed to sync imported projects to API:', error);
          return { success: false, error: `Import saved locally but failed to sync to server: ${error.message}` };
        }
      }

      return { success: true, projects };
    }
    return { success: false, error: 'Invalid format: expected array' };
  } catch (e) {
    return { success: false, error: e.message };
  }
};

